import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Flex,
  Input,
  Modal,
  Progress,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  BulbOutlined,
  ExperimentOutlined,
  NodeIndexOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAiAgent } from '@/contexts/AiAgentContext';
import { useIntelligenceContext } from '@/hooks/useIntelligenceContext';
import { intelligenceService } from '@/services/intelligence.service';
import { digitalTwinService } from '@/services/digitalTwin.service';
import { RelationshipExplorer } from './RelationshipExplorer';

const { Text, Title, Paragraph } = Typography;

function severityColor(s: string) {
  if (s === 'critical') return 'red';
  if (s === 'watch') return 'orange';
  return 'blue';
}

export function IntelligenceSidebar() {
  const { intelligenceSidebarOpen, setIntelligenceSidebarOpen, pageContext } = useAiAgent();
  const [relationshipOpen, setRelationshipOpen] = useState(false);
  const [simModalOpen, setSimModalOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [scenarioType, setScenarioType] = useState<string>('supplier_delay');
  const [actionTitle, setActionTitle] = useState('Follow up from intelligence sidebar');
  const [isNarrow, setIsNarrow] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    const sync = () => setIsNarrow(window.innerWidth < 992);
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);

  const hasDoc = !!(pageContext.docType || pageContext.docId || pageContext.module);
  const { data: pack, isLoading, isError, refetch, isFetching } = useIntelligenceContext({
    enabled: intelligenceSidebarOpen && hasDoc,
    includeSimulation: true,
  });

  const catalogQ = useQueryCatalog(intelligenceSidebarOpen);

  useEffect(() => {
    if (intelligenceSidebarOpen && hasDoc) {
      void intelligenceService.trackAdoption({
        eventType: 'sidebar_open',
        moduleCode: pageContext.module,
        docType: pageContext.docType,
        docId: pageContext.docId,
      });
    }
  }, [intelligenceSidebarOpen, hasDoc, pageContext.module, pageContext.docType, pageContext.docId]);

  const simMut = useMutation({
    mutationFn: () =>
      intelligenceService.simulate({
        scenarioType,
        title: `Embedded: ${scenarioType}`,
        confirm: true,
      }),
    onSuccess: () => {
      message.success('Simulation completed (advisory only)');
      setSimModalOpen(false);
      void qc.invalidateQueries({ queryKey: ['intelligence-context'] });
    },
    onError: (e: any) => message.error(e?.message || 'Simulation failed'),
  });

  const actionMut = useMutation({
    mutationFn: () =>
      intelligenceService.createAction({
        title: actionTitle,
        description: 'Created from Universal Intelligence Sidebar with explicit confirmation.',
        domain: pageContext.module || 'operations',
        docType: pageContext.docType,
        docId: pageContext.docId,
        confirm: true,
      }),
    onSuccess: () => {
      message.success('Action created in EOAF');
      setActionModalOpen(false);
      void qc.invalidateQueries({ queryKey: ['intelligence-context'] });
      void intelligenceService.trackAdoption({
        eventType: 'rec_accept',
        moduleCode: pageContext.module,
        docType: pageContext.docType,
        docId: pageContext.docId,
      });
    },
    onError: (e: any) => message.error(e?.message || 'Create action failed'),
  });

  const explainMut = useMutation({
    mutationFn: () => intelligenceService.explain(pageContext),
    onSuccess: () => {
      message.success('Explain audit recorded');
      void refetch();
    },
  });

  const trust = pack?.meta?.trust || '—';
  const health = Math.round(pack?.meta?.moduleHealth || 0);

  return (
    <>
      <Drawer
        title={
          <Flex align="center" gap={8}>
            <BulbOutlined />
            <span>Intelligence</span>
            {pageContext.docType ? <Tag>{pageContext.docType}</Tag> : null}
          </Flex>
        }
        placement={isNarrow ? 'bottom' : 'right'}
        height={isNarrow ? '85vh' : undefined}
        width={isNarrow ? undefined : 420}
        open={intelligenceSidebarOpen}
        onClose={() => setIntelligenceSidebarOpen(false)}
        destroyOnClose={false}
        extra={
          <Space>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={isFetching}
              onClick={() => void refetch()}
            />
            <Button size="small" onClick={() => explainMut.mutate()} loading={explainMut.isPending}>
              Explain
            </Button>
          </Space>
        }
      >
        {!hasDoc ? (
          <Empty description="Open a document or module page to load intelligence context." />
        ) : isLoading ? (
          <Paragraph type="secondary">Loading unified context…</Paragraph>
        ) : isError ? (
          <Alert type="warning" showIcon message="Intelligence temporarily unavailable (soft-fail)." />
        ) : pack?.meta?.degraded ? (
          <Alert type="info" showIcon message="Intelligence context degraded or disabled." />
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <section>
              <Title level={5}>Context</Title>
              <Text type="secondary">
                {pack?.entity?.module || 'general'}
                {pack?.summary
                  ? ` · ${(pack.summary as any).title || (pack.summary as any).status || 'summary'}`
                  : ''}
              </Text>
              <div style={{ marginTop: 8 }}>
                <Text style={{ fontSize: 12 }}>Module health</Text>
                <Progress percent={health} size="small" status={health < 50 ? 'exception' : 'active'} />
              </div>
            </section>

            <section>
              <Title level={5}>Risks & signals</Title>
              {(pack?.signals || []).length === 0 ? (
                <Text type="secondary">No active signals.</Text>
              ) : (
                (pack?.signals || []).slice(0, 8).map((s) => (
                  <div key={s.id} style={{ marginBottom: 8 }}>
                    <Tag color={severityColor(s.severity)}>{s.severity}</Tag>
                    <Text strong>{s.title}</Text>
                    {s.detail ? (
                      <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
                        {s.detail}
                      </Paragraph>
                    ) : null}
                  </div>
                ))
              )}
            </section>

            <section>
              <Title level={5}>Recommendations</Title>
              {(pack?.recommendations || []).length === 0 ? (
                <Text type="secondary">No recommendations.</Text>
              ) : (
                (pack?.recommendations || []).slice(0, 8).map((r) => (
                  <div key={r.id} style={{ marginBottom: 10 }}>
                    <Text strong>{r.title}</Text>
                    <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
                      {r.rationale}
                    </Paragraph>
                    <Tag>{r.source}</Tag>
                  </div>
                ))
              )}
            </section>

            <section>
              <Title level={5}>Knowledge</Title>
              {(pack?.knowledge || []).length === 0 ? (
                <Text type="secondary">No linked memories.</Text>
              ) : (
                (pack?.knowledge || []).slice(0, 5).map((k) => (
                  <div key={k.id} style={{ marginBottom: 8 }}>
                    <Text strong>{k.title}</Text>
                    <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
                      {k.snippet}
                    </Paragraph>
                  </div>
                ))
              )}
            </section>

            <section>
              <Title level={5}>Actions</Title>
              <Space wrap style={{ marginBottom: 8 }}>
                <Button size="small" icon={<PlusOutlined />} onClick={() => setActionModalOpen(true)}>
                  Create action
                </Button>
              </Space>
              {(pack?.actions || []).length === 0 ? (
                <Text type="secondary">No open EOAF actions.</Text>
              ) : (
                (pack?.actions || []).slice(0, 6).map((a) => (
                  <div key={a.id} style={{ marginBottom: 6 }}>
                    <Tag>{a.status}</Tag>
                    <Text>{a.title}</Text>
                  </div>
                ))
              )}
            </section>

            <section>
              <Title level={5}>Simulation</Title>
              <Space wrap>
                <Button size="small" icon={<ExperimentOutlined />} onClick={() => setSimModalOpen(true)}>
                  What happens if?
                </Button>
                <Button
                  size="small"
                  icon={<NodeIndexOutlined />}
                  disabled={!pageContext.docType || !pageContext.docId}
                  onClick={() => setRelationshipOpen(true)}
                >
                  Relationships
                </Button>
              </Space>
              {(pack?.simulations || []).slice(0, 4).map((s) => (
                <div key={s.id} style={{ marginTop: 8 }}>
                  <Text>{s.name}</Text> <Tag>{s.status}</Tag>
                </div>
              ))}
            </section>

            <section>
              <Title level={5}>Trust & evidence</Title>
              <Tag color={trust === 'high' ? 'green' : trust === 'degraded' ? 'red' : 'gold'}>{trust}</Tag>
              <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
                Sources: {(pack?.meta?.sourcesSucceeded || []).join(', ') || '—'}
                {pack?.meta?.elapsedMs != null ? ` · ${pack.meta.elapsedMs}ms` : ''}
                {pack?.meta?.cacheHit ? ' · cache' : ''}
              </Paragraph>
              {(pack?.meta?.analyzerErrors || []).length > 0 ? (
                <Alert
                  type="warning"
                  showIcon
                  message="Some analyzers soft-failed"
                  description={(pack?.meta?.analyzerErrors || []).slice(0, 3).join('; ')}
                />
              ) : null}
            </section>
          </Space>
        )}
      </Drawer>

      <Modal
        title="What happens if? (confirm)"
        open={simModalOpen}
        onCancel={() => setSimModalOpen(false)}
        onOk={() => simMut.mutate()}
        confirmLoading={simMut.isPending}
        okText="Run simulation"
      >
        <Paragraph>
          Runs an advisory Digital Twin scenario. Never posts ERP transactions. Requires explicit
          confirmation.
        </Paragraph>
        <Select
          style={{ width: '100%' }}
          value={scenarioType}
          onChange={setScenarioType}
          options={(catalogQ.data || []).map((c: any) => ({
            value: c.code || c.scenarioType,
            label: c.title || c.code,
          }))}
        />
      </Modal>

      <Modal
        title="Create action (confirm)"
        open={actionModalOpen}
        onCancel={() => setActionModalOpen(false)}
        onOk={() => actionMut.mutate()}
        confirmLoading={actionMut.isPending}
        okText="Create EOAF action"
      >
        <Paragraph>
          Creates an operational action for people to execute. Does not auto-approve or post ERP.
        </Paragraph>
        <Input value={actionTitle} onChange={(e) => setActionTitle(e.target.value)} />
      </Modal>

      {pageContext.docType && pageContext.docId ? (
        <RelationshipExplorer
          open={relationshipOpen}
          onClose={() => setRelationshipOpen(false)}
          entityType={pageContext.docType}
          entityId={pageContext.docId}
        />
      ) : null}
    </>
  );
}

function useQueryCatalog(enabled: boolean) {
  const [data, setData] = useState<any[]>([]);
  useEffect(() => {
    if (!enabled) return;
    void digitalTwinService
      .getCatalog()
      .then((rows) => setData(Array.isArray(rows) ? rows : []))
      .catch(() => setData([{ code: 'supplier_delay', title: 'Supplier delay' }]));
  }, [enabled]);
  return useMemo(() => ({ data }), [data]);
}
